import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Link2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { analytics } from "@/lib/analytics";
import { useShortLink } from "@/hooks/useShortLink";
import { useT } from "@/i18n";

type Props = {
  /** Absolute or root-relative page path, e.g. "/user/deejaypico" */
  path: string;
  /** Referral code appended as ?ref= */
  refCode?: string | null;
  title: string;
  className?: string;
};

export function shareUrl(path: string, refCode?: string | null) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://behot.now";
  const url = new URL(path, origin);
  if (refCode) url.searchParams.set("ref", refCode);
  return url.toString();
}

export function ShareButtons({ path, refCode, title, className }: Props) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const url = shareUrl(useShortLink(path), refCode);
  const encoded = encodeURIComponent(url);
  const text = encodeURIComponent(title);

  const targets = [
    { label: "X", href: `https://twitter.com/intent/tweet?text=${text}&url=${encoded}` },
    { label: "WhatsApp", href: `https://wa.me/?text=${text}%20${encoded}` },
    { label: "Telegram", href: `https://t.me/share/url?url=${encoded}&text=${text}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
  ];

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${title}\n${url}`);
      setCopied(true);
      analytics.share({ method: "copy_link", contentType: "page", itemId: path });
      toast.success(t("profiles.linkCopiedWithReferral"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("profiles.couldNotCopyLink"));
    }
  }

  async function nativeShare() {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, text: title, url });
        analytics.share({ method: "native", contentType: "page", itemId: path });
        return;
      } catch {
        /* dismissed */
      }
    }
    void copy();
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="hot" size="sm" onClick={() => void nativeShare()}>
          <Share2 className="size-4" /> {t("profiles.share")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {t("profiles.copyLink")}
        </Button>
        {targets.map((t) => (
          <Button key={t.label} asChild variant="ghost" size="sm">
            <a
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                analytics.share({
                  method: t.label.toLowerCase(),
                  contentType: "page",
                  itemId: path,
                })
              }
            >
              {t.label}
            </a>
          </Button>
        ))}
      </div>
      {refCode && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link2 className="size-3.5" /> {t("profiles.sharedLinksCarryReferral")}{" "}
          <span className="font-medium">{refCode}</span> — {t("profiles.earnHeatOnSignup")}
        </p>
      )}
    </div>
  );
}

/** Compact share menu used on board rows so holders can brag about their rank. */
export function ShareRankButton({
  path,
  refCode,
  title,
  label,
  className,
}: Props & { label?: string }) {
  const { t } = useT();
  const resolvedLabel = label ?? t("profiles.share");
  const url = shareUrl(useShortLink(path), refCode);
  const encoded = encodeURIComponent(url);
  const text = encodeURIComponent(title);

  const targets = [
    { label: "X", href: `https://twitter.com/intent/tweet?text=${text}&url=${encoded}` },
    { label: "WhatsApp", href: `https://wa.me/?text=${text}%20${encoded}` },
    { label: "Telegram", href: `https://t.me/share/url?url=${encoded}&text=${text}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
  ];

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${title}\n${url}`);
      analytics.share({ method: "copy_link", contentType: "board_rank", itemId: path });
      toast.success(t("profiles.rankLinkCopiedWithReferral"));
    } catch {
      toast.error(t("profiles.couldNotCopyLink"));
    }
  }

  async function nativeShare() {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, text: title, url });
        analytics.share({ method: "native", contentType: "board_rank", itemId: path });
        return;
      } catch {
        /* dismissed */
      }
    }
    void copy();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={className}
          aria-label={t("profiles.shareThisRank")}
        >
          <Share2 className="size-3.5" />
          {resolvedLabel ? <span className="hidden sm:inline">{resolvedLabel}</span> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onSelect={() => void nativeShare()}>
          <Share2 className="size-4" /> {t("profiles.shareRank")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void copy()}>
          <Copy className="size-4" /> {t("profiles.copyLink")}
        </DropdownMenuItem>
        {targets.map((t) => (
          <DropdownMenuItem key={t.label} asChild>
            <a
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                analytics.share({
                  method: t.label.toLowerCase(),
                  contentType: "board_rank",
                  itemId: path,
                })
              }
            >
              {t.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
